import { h, Component, cloneElement } from 'preact';
import debounce from '@os/debounce';

function watchPositionsChanged(watchPositionsOld, watchPositionsNew) {
	//If both are falsy, there has been no change:
	if (!watchPositionsOld && !watchPositionsNew) return false;
	//If one is falsy and one is not there was definitely change:
	if ((watchPositionsOld && !watchPositionsNew) || (!watchPositionsOld && watchPositionsNew)) return true;
	//If both are arrays, we need to check the contents for equality:
	let oldKeys = watchPositionsOld.map(obj => obj.y).sort(),
		newKeys = watchPositionsNew.map(obj => obj.y).sort();
	return oldKeys.reduce((acc, val, i) => acc || val !== newKeys[i], false);
}

/**
 * ScrollDirection is used to detect changes in the user's scrolling direction.
 *
 * NOTE: Currently only vertical scrolling is supported.
 *
 * @param {Number=10} props.debounce A value in milliseconds to debounce incoming scroll events.
 *
 * This component's first child will be given the following props:
 * @property {Boolean} initialScrollDirection If true, consider the user to have not yet scrolled.
 * @property {Number} scrollDirectionVertical -1 user scrolled up the page, 1 user scrolled down the page, 0 no change in scroll value.
 * @property {Function} resetScrollDirectionVertical Call this to reset scrollDirectionVertical to 0.
 */
export class ScrollDirection extends Component {

	state = {
		scrollDirectionVertical: 0,
		initialRender: true,
		hasRendered: false
	}

	static defaultProps = {
		debounceInit: 100,
		debounce: 10
	}

	resetPosition = () => {
		this.setState({ scrollDirectionVertical: 0 });
	}

	processScroll = (e, force) => {

		/*
		ONLY VERTICAL SCROLLING SUPPORTED CURRENTLY
		*/

		let lastKnownPositionVertical = this.scrollPositionVertical;
		const scrollingElement = () => document.scrollingElement || document.body;
		const getOffset = () => (window.scrollY || window.pageYOffset || scrollingElement().scrollTop);
		let newPositionVertical = getOffset();

		let scrollHeight = Math.max(
			document.body.scrollHeight, document.documentElement.scrollHeight,
			document.body.offsetHeight, document.documentElement.offsetHeight,
			document.body.clientHeight, document.documentElement.clientHeight
		) - window.innerHeight;

		
		if (newPositionVertical < 0) newPositionVertical = 0;
		if (newPositionVertical > scrollHeight) newPositionVertical = scrollHeight;
	
		if (!force && lastKnownPositionVertical === newPositionVertical) {
			return;
		}
		if (this.props.onScroll) this.props.onScroll(newPositionVertical);
		let scrollDiffVertical = newPositionVertical - lastKnownPositionVertical;
		let scrollDirectionVertical = scrollDiffVertical > 0 ? 1 : scrollDiffVertical < 0 ? -1 : 0;
		this.scrollPositionVertical = newPositionVertical;

		let initialRender = !this.state.hasRendered;
		let stateObj = {
			initialRender,
			hasRendered: true
		};
		if (scrollDirectionVertical !== this.state.scrollDirectionVertical) {
			stateObj.scrollDirectionVertical =  scrollDirectionVertical;
			this.setState(stateObj);
			if (this.props.onDirectionChange) this.props.onDirectionChange(stateObj);
		}

		if (this.watchPositions) {
			Object.keys(this.watchPositions).map(key => this.watchPositions[key]).forEach(pos => {
				let dif = pos.y - newPositionVertical;
				let dir = dif > 0 ? 1 : dif < 0 ? -1 : 0;
				if (pos.dir !== dir) {
					pos.dir = dir;
					if (this.props.onWatchPositionChange) this.props.onWatchPositionChange(pos);
				}
			});
		}
		this.debounceGate = true;
	}

	handleScroll = () => this.debounceGate ? this.handleAfterInitScroll() : this.handleInitScroll();
	handleInitScroll = debounce(this.processScroll, this.props.debounceInit);
	handleAfterInitScroll = this.props.debounce ? debounce(this.processScroll, this.props.debounce): this.processScroll;

	componentDidMount() {
		window.addEventListener('scroll', this.handleScroll);
		this.handleScroll();
	}

	componentWillReceiveProps(nextProps) {
		if (watchPositionsChanged(nextProps.watchPositions, this.lastWatchPositions)) {
			if (!this.watchPositions) this.watchPositions = {};
			let newWatchPositions = {};
			nextProps.watchPositions.forEach(pos => {
				let name = `y${pos.y}`;
				newWatchPositions[name] = this.watchPositions[name] || { y: pos.y };
			});
			this.lastWatchPositions = nextProps.watchPositions;
			this.watchPositions = newWatchPositions;
			this.processScroll(null, true);

		}
		else if ((!nextProps.watchPositions || nextProps.watchPositions.length === 0) && this.watchPositions) {
			this.watchPositions = null;
			this.lastWatchPositions = null;
			this.processScroll(null, true);
		}
	}

	componentWillUnmount() {
		window.removeEventListener('scroll', this.handleScroll);
	}

	render({ children }, { scrollDirectionVertical, initialRender }) {
		return (children && children.length)
			? cloneElement(children[0], { scrollDirectionVertical, resetScrollDirectionVertical: this.resetPosition, initialScrollDirection: initialRender })
			: null;
	}
}

/**
 * Wraps a component with a ScrollDirection component.
 * @param {Number=10} args.debounce A value in milliseconds to debounce incoming scroll events.
 */
export default function scrollDirection(args) {
	return function (Child) {
		function Wrapper(props) {
			return (
				<ScrollDirection {...args}>
					<Child {...props} />
				</ScrollDirection>
			);
		}
		Wrapper.getWrappedComponent = Child && Child.getWrappedComponent || (() => Child);
		return Wrapper;
	};
}